import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";

const MAX_POLLS = 8;
const INTERVAL_MS = 1800;

export default function SponsorSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data: d } = await api.get(`/sponsor/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (d.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (_) {
        if (attempts >= MAX_POLLS) {
          setStatus("error");
          return;
        }
      }
      if (attempts < MAX_POLLS) {
        setTimeout(poll, INTERVAL_MS);
      } else {
        setStatus("timeout");
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; referenced values are stable module imports/constants
  }, [sessionId]);

  return (
    <div data-testid="sponsor-success-page" className="bg-white text-[#0a0a0a] min-h-screen flex flex-col">
      <Seo title="Sponsor confirmation — Universtrend" description="Your sponsorship is being confirmed." />
      <Header />
      <main className="flex-1 flex items-center">
        <div className="mx-auto max-w-[900px] w-full px-6 lg:px-10 py-16">
          {status === "polling" && (
            <div className="text-center" data-testid="success-polling">
              <div className="overline text-[#52525b] mb-4">Confirming your payment…</div>
              <h1 className="font-display font-extrabold text-5xl lg:text-7xl tracking-tighter">Hang tight.</h1>
              <p className="font-mono text-sm text-[#52525b] mt-4">Talking to Stripe…</p>
            </div>
          )}
          {status === "paid" && (
            <div className="text-center" data-testid="success-paid">
              <div className="overline text-[#52525b] mb-4">You&apos;re live ✓</div>
              <h1 className="font-display font-extrabold text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
                Thank you.<br /><span className="text-[#ff3b30]">Your brand is on the homepage.</span>
              </h1>
              <p className="text-base text-[#52525b] mt-6 max-w-xl mx-auto">
                Your sponsorship of <b>${data?.amount?.toFixed(2)}</b> is confirmed. The slot has been activated and is visible to every reader landing on Universtrend.
              </p>
              <Link to="/" data-testid="success-home-link" className="hard-shadow inline-block mt-10 border border-black bg-black text-white px-6 py-3 overline">
                See your slot →
              </Link>
            </div>
          )}
          {(status === "expired" || status === "error" || status === "timeout") && (
            <div className="text-center" data-testid="success-failed">
              <div className="overline text-[#52525b] mb-4">Hmm</div>
              <h1 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tighter">
                We couldn&apos;t confirm the payment.
              </h1>
              <p className="text-sm text-[#52525b] mt-6">
                If you were charged, please email us — otherwise try again.
              </p>
              <Link to="/sponsor" className="overline mt-8 inline-block border border-black px-5 py-2.5 hover:bg-black hover:text-white">
                ← Back to checkout
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
